import { useEffect, useState } from "react";
import { api } from "@/lib/api";

// Company branding for this browser (active company for staff; portal domain / preview path otherwise).
// Bad Ass ("primary") keeps its built-in logos and colours exactly.
let cache = null;
const subs = new Set();

export const loadBrand = (force = false) => {
  if (!cache || force) {
    cache = api.get("/public/branding").then((r) => r.data).catch(() => ({ primary: false, name: "" }));  // neutral on failure — never another company's brand
    cache.then(applyBrand).then(() => subs.forEach((f) => f()));
  }
  return cache;
};

function applyBrand(b) {
  const root = document.documentElement.style;
  if (!b.primary && b.colors?.primary) root.setProperty("--brand-dark", b.colors.primary);
  else root.removeProperty("--brand-dark");
  window.__brandName = b.name || "Bad Ass Horse Sales";
  window.__brandShort = b.primary ? "Bad Ass" : b.short_name || b.name || "";
  window.__auctionClose = b.auction_close_label || "";
  window.__auctionTz = b.auction_time_zone || "America/Edmonton";
  const t = document.title;
  document.title = t.includes("|") ? t.replace(/[^|]*$/, ` ${window.__brandName}`) : t && t !== "Bad Ass Horse Sales" ? `${t} | ${window.__brandName}` : window.__brandName;
}

const logoSrc = (b) => (b.logo_url?.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${b.logo_url}` : b.logo_url);

export function useBrand() {
  const [b, setB] = useState(null);
  useEffect(() => {
    const f = () => cache.then(setB);
    subs.add(f);
    loadBrand().then(setB);
    return () => { subs.delete(f); };
  }, []);
  return b; // null until this browser's company branding has loaded — never show another company's brand meanwhile
}

export const brandLogo = (b, fallback) => (b && !b.primary && b.logo_url ? logoSrc(b) : fallback);

export function BrandName() {
  return useBrand()?.name || "";
}

// Short company name for UI copy ("Bad Ass" for Bad Ass; else the company's short name or name).
export function BrandShort() {
  const b = useBrand();
  return b ? (b.primary ? "Bad Ass" : b.short_name || b.name || "") : "";
}

// Company auction closing time, e.g. "8:00 PM Mountain Time" (Bad Ass).
export function AuctionClose() {
  return useBrand()?.auction_close_label || "";
}

// "8:00 PM Mountain Time" -> "8:00 PM MT"; no time given -> the company's default close.
export const closeShort = (t) => (t || window.__auctionClose || "8:00 PM Mountain Time").replace(" Mountain Time", " MT");

// Plain-string versions for messages and file names built after branding has loaded.
export const brandName = () => window.__brandName || "";
export const brandShort = () => window.__brandShort || brandName();

// /c/{slug}/... = preview path for a company before its portal domain is connected.
export const COMPANY_PATH_KEY = "company_path";
